import React, { useState, useEffect, useRef } from 'react'
import DashboardLayout from '@/components/DashboardLayout'
import GlassCard from '@/components/ui/GlassCard'
import api from '@/services/api'
import {
  Users, Star, Loader2, Calendar, Building2,
  ChevronDown, Trophy, Target, Eye, Home,
  Smartphone, Monitor, Globe, TrendingUp, HelpCircle, Printer
} from 'lucide-react'
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts'

interface Seller { id: string; name: string; subdomain: string; avatar_url: string | null; sessions: string; visitors: string; leads: string; properties: string; avg_score: string; closed_won: string }
interface TrafficSource { source: string; sessions: number; leads: number }
interface Device { type: string; sessions: number; percentage: number }
interface TopProperty { id: string; title: string; seoTitle: string; agentName: string; interactions: number; uniqueViews: number }
interface SourceByAgent { agentId: string; source: string; leads: number }
interface DeviceByAgent { agentId: string; deviceType: string; sessions: number }

interface AgencyData {
  totals: { sessions: number; visitors: number; leads: number; properties: number; avgScore: string }
  sellers: Seller[]
  trafficSources: TrafficSource[]
  devices: Device[]
  topProperties: TopProperty[]
  sourcesByAgent: SourceByAgent[]
  devicesByAgent: DeviceByAgent[]
}

const COLORS = ['#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16']
const SOURCE_COLORS: Record<string, string> = { facebook: '#1877f2', instagram: '#e4405f', google: '#4285f4', tiktok: '#000000', twitter: '#1da1f2', linkedin: '#0077b5', direct: '#6b7280', qr: '#10b981', whatsapp: '#25d366', chatbot: '#3b82f6' }

// Componente Tooltip con ?
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle className="h-3 w-3 text-white/40 hover:text-white/70 cursor-help" />
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[9999] pointer-events-none border border-white/20 shadow-xl">
      {text}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-900" />
    </div>
  </div>
)

const AgencyAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<AgencyData | null>(null)
  const [timeRange, setTimeRange] = useState('30')
  const [selectedSeller, setSelectedSeller] = useState<string>('all')
  const [error, setError] = useState<string | null>(null)
  const [showSellerDropdown, setShowSellerDropdown] = useState(false)
  const printRef = useRef<HTMLDivElement>(null)

  useEffect(() => { fetchAgencyData() }, [timeRange])

  const fetchAgencyData = async () => {
    setLoading(true); setError(null)
    try {
      const response = await api.get(`/api/agents/analytics/agency-overview?days=${timeRange}`)
      if (response.data.success) setData(response.data.data)
      else setError('Error al cargar datos')
    } catch (err: any) {
      setError(err.response?.status === 403 ? 'Sin acceso' : 'Error al cargar')
    } finally { setLoading(false) }
  }

  const handlePrint = () => { window.print() }

  const getMetrics = () => {
    if (!data) return null
    if (selectedSeller === 'all') {
      return { sessions: data.totals.sessions, visitors: data.totals.visitors, leads: data.totals.leads, properties: data.totals.properties, avgScore: parseFloat(data.totals.avgScore) || 0, sellersCount: data.sellers.length }
    } else {
      const seller = data.sellers.find(s => s.id === selectedSeller)
      if (!seller) return null
      return { sessions: parseInt(seller.sessions) || 0, visitors: parseInt(seller.visitors) || 0, leads: parseInt(seller.leads) || 0, properties: parseInt(seller.properties) || 0, avgScore: parseFloat(seller.avg_score) || 0, closedWon: parseInt(seller.closed_won) || 0 }
    }
  }

  const metrics = getMetrics()
  const selectedSellerData = selectedSeller !== 'all' ? data?.sellers.find(s => s.id === selectedSeller) : null

  // Datos para gráficas
  const leadsDistribution = data?.sellers.filter(s => parseInt(s.leads) > 0).map(s => ({ name: s.name.split(' ')[0], leads: parseInt(s.leads), fullName: s.name })) || []
  const sellerRanking = data?.sellers.map(s => ({ ...s, leadsNum: parseInt(s.leads) || 0, sessionsNum: parseInt(s.sessions) || 0, scoreNum: parseFloat(s.avg_score) || 0 })).sort((a, b) => b.leadsNum - a.leadsNum) || []

  // Fuentes y dispositivos filtrados por agente
  const filteredSources = selectedSeller === 'all' ? data?.trafficSources : data?.sourcesByAgent.filter(s => s.agentId === selectedSeller).map(s => ({ source: s.source, leads: s.leads, sessions: 0 })) || []
  const filteredDevices = selectedSeller === 'all' ? data?.devices : (() => {
    const agentDevices = data?.devicesByAgent.filter(d => d.agentId === selectedSeller) || []
    const total = agentDevices.reduce((sum, d) => sum + d.sessions, 0)
    return agentDevices.map(d => ({ type: d.deviceType, sessions: d.sessions, percentage: total > 0 ? Math.round((d.sessions / total) * 100) : 0 }))
  })()

  return (
    <DashboardLayout>
      <div ref={printRef} className="space-y-4 md:space-y-6 print:bg-white print:text-black">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white print:text-black mb-1 flex items-center gap-2">
                <Building2 className="h-6 w-6 md:h-8 md:w-8 text-blue-400 print:text-blue-600" />
                Analytics de Agencia
              </h1>
              <p className="text-gray-300 print:text-gray-600 text-sm md:text-base">
                {selectedSeller === 'all' ? 'Métricas globales de tu equipo' : `Métricas de ${selectedSellerData?.name || 'agente'}`}
              </p>
            </div>
            <button onClick={handlePrint} className="print:hidden p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-all" title="Imprimir reporte">
              <Printer className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 print:hidden">
            {/* Selector de Agente */}
            <div className="relative flex-1 sm:flex-none">
              <button onClick={() => setShowSellerDropdown(!showSellerDropdown)} className="w-full sm:w-auto flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-all min-w-[180px] justify-between text-sm">
                <span className="truncate">{selectedSeller === 'all' ? '👥 Todos' : selectedSellerData?.name?.split(' ')[0] || 'Seleccionar'}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showSellerDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showSellerDropdown && (
                <div className="absolute top-full mt-1 w-full bg-slate-800 border border-white/20 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                  <button onClick={() => { setSelectedSeller('all'); setShowSellerDropdown(false) }} className={`w-full px-3 py-2 text-left hover:bg-white/10 text-sm flex items-center gap-2 ${selectedSeller === 'all' ? 'bg-blue-500/20 text-blue-400' : 'text-white'}`}><Users className="h-4 w-4" /> Todos</button>
                  {data?.sellers.map(seller => (
                    <button key={seller.id} onClick={() => { setSelectedSeller(seller.id); setShowSellerDropdown(false) }} className={`w-full px-3 py-2 text-left hover:bg-white/10 text-sm flex items-center gap-2 ${selectedSeller === seller.id ? 'bg-blue-500/20 text-blue-400' : 'text-white'}`}>
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-xs font-bold">{seller.name.charAt(0)}</div>
                      <span className="truncate">{seller.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Selector de Período */}
            <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
              <Calendar className="h-4 w-4 text-white/60 ml-1" />
              {['7', '30', '90'].map(days => (
                <button key={days} onClick={() => setTimeRange(days)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${timeRange === days ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' : 'text-white/70 hover:bg-white/10'}`}>{days}d</button>
              ))}
            </div>
          </div>
        </div>

        {loading && <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 text-blue-500 animate-spin" /></div>}
        {error && <GlassCard className="p-4 border-red-500/30"><p className="text-red-400 text-sm">{error}</p></GlassCard>}

        {!loading && data && metrics && (
          <>
            {/* Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-4">
              {[
                { label: 'Sesiones', value: metrics.sessions, icon: Eye, color: 'bg-blue-500/20', iconColor: 'text-blue-400', tooltip: 'Visitas totales a las páginas de tus agentes' },
                { label: 'Visitantes', value: metrics.visitors, icon: Users, color: 'bg-cyan-500/20', iconColor: 'text-cyan-400', tooltip: 'Personas únicas que visitaron (por cookie)' },
                { label: 'Leads', value: metrics.leads, icon: Target, color: 'bg-green-500/20', iconColor: 'text-green-400', tooltip: 'Contactos capturados por el chatbot IA' },
                { label: 'Propiedades', value: metrics.properties, icon: Home, color: 'bg-purple-500/20', iconColor: 'text-purple-400', tooltip: 'Propiedades activas publicadas' },
                { label: 'Score', value: metrics.avgScore.toFixed(0), icon: Star, color: 'bg-yellow-500/20', iconColor: 'text-yellow-400', tooltip: 'Calificación promedio de leads (0-100)' },
                { label: selectedSeller === 'all' ? 'Agentes' : 'Cerrados', value: selectedSeller === 'all' ? metrics.sellersCount : (metrics as any).closedWon || 0, icon: selectedSeller === 'all' ? Building2 : Trophy, color: 'bg-pink-500/20', iconColor: 'text-pink-400', tooltip: selectedSeller === 'all' ? 'Agentes en tu equipo' : 'Leads con status cerrado ganado' }
              ].map((m, i) => (
                <GlassCard key={i} className="p-3 md:p-4 print:border print:border-gray-300">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white/60 print:text-gray-500 text-xs mb-0.5 flex items-center">{m.label}<InfoTooltip text={m.tooltip} /></p>
                      <p className="text-xl md:text-2xl font-bold text-white print:text-black">{m.value}</p>
                    </div>
                    <div className={`p-1.5 md:p-2 rounded-lg ${m.color} print:bg-gray-100`}><m.icon className={`h-4 w-4 md:h-5 md:w-5 ${m.iconColor} print:text-gray-600`} /></div>
                  </div>
                </GlassCard>
              ))}
            </div>

            {/* Fuentes + Dispositivos (siempre visibles) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <GlassCard className="p-4 print:border print:border-gray-300">
                <h3 className="text-base font-semibold text-white print:text-black mb-3 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-400" /> Fuentes de Leads <InfoTooltip text="Origen de los leads capturados. 'chatbot' = capturado por IA, 'direct' = sin fuente identificada" />
                </h3>
                {filteredSources && filteredSources.length > 0 ? (
                  <div className="space-y-2">
                    {filteredSources.slice(0, 6).map((source, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SOURCE_COLORS[source.source] || COLORS[i % COLORS.length] }} />
                          <span className="text-white print:text-black text-sm capitalize">{source.source}</span>
                        </div>
                        <span className="text-green-400 print:text-green-600 text-sm font-medium">{source.leads} leads</span>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-white/10 print:border-gray-300 flex justify-between">
                      <span className="text-white/60 print:text-gray-500 text-sm">Total</span>
                      <span className="text-white print:text-black font-bold">{filteredSources.reduce((sum, s) => sum + s.leads, 0)} leads</span>
                    </div>
                  </div>
                ) : <p className="text-white/40 print:text-gray-400 text-sm text-center py-4">Sin datos de fuentes</p>}
              </GlassCard>

              <GlassCard className="p-4 print:border print:border-gray-300">
                <h3 className="text-base font-semibold text-white print:text-black mb-3 flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-purple-400" /> Dispositivos <InfoTooltip text="Tipo de dispositivo usado por los visitantes" />
                </h3>
                {filteredDevices && filteredDevices.length > 0 ? (
                  <div className="space-y-3">
                    {filteredDevices.map((device, i) => (
                      <div key={i} className="flex items-center gap-2">
                        {device.type === 'mobile' ? <Smartphone className="h-4 w-4 text-blue-400" /> : <Monitor className="h-4 w-4 text-purple-400" />}
                        <div className="flex-1">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-white print:text-black capitalize">{device.type}</span>
                            <span className="text-white/60 print:text-gray-500">{device.percentage}% ({device.sessions})</span>
                          </div>
                          <div className="h-2 bg-white/10 print:bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${device.type === 'mobile' ? 'bg-blue-500' : 'bg-purple-500'}`} style={{ width: `${device.percentage}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-white/40 print:text-gray-400 text-sm text-center py-4">Sin datos de dispositivos</p>}
              </GlassCard>
            </div>

            {selectedSeller === 'all' ? (
              <>
                {/* Distribución Leads + Ranking */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <GlassCard className="p-4 print:border print:border-gray-300">
                    <h3 className="text-base font-semibold text-white print:text-black mb-3 flex items-center gap-2">
                      <Target className="h-4 w-4 text-green-400" /> Distribución de Leads <InfoTooltip text="Porcentaje de leads por agente. Solo muestra agentes con leads > 0" />
                    </h3>
                    {leadsDistribution.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={leadsDistribution} cx="50%" cy="50%" outerRadius={70} dataKey="leads" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            {leadsDistribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #ffffff20', borderRadius: '8px', fontSize: '12px' }} formatter={(value: number, name: string, props: any) => [`${value} leads`, props.payload.fullName]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p className="text-white/40 text-sm text-center py-8">Sin leads en el período</p>}
                  </GlassCard>

                  <GlassCard className="p-4 print:border print:border-gray-300">
                    <h3 className="text-base font-semibold text-white print:text-black mb-3 flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-yellow-400" /> Ranking por Leads <InfoTooltip text="Ordenado por cantidad de leads capturados en el período" />
                    </h3>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {sellerRanking.map((seller, i) => (
                        <div key={seller.id} onClick={() => setSelectedSeller(seller.id)} className="flex items-center gap-2 p-2 bg-white/5 print:bg-gray-50 rounded-lg hover:bg-white/10 cursor-pointer print:cursor-default">
                          <span className={`text-sm font-bold w-5 ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-amber-600' : 'text-white/40'} print:text-gray-600`}>{i + 1}</span>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold print:bg-blue-100 print:text-blue-600">{seller.name.charAt(0)}</div>
                          <div className="flex-1 min-w-0"><p className="text-white print:text-black text-sm truncate">{seller.name}</p></div>
                          <div className="text-right"><p className="text-green-400 print:text-green-600 text-sm font-bold">{seller.leadsNum} leads</p></div>
                        </div>
                      ))}
                    </div>
                  </GlassCard>
                </div>

                {/* Top Propiedades */}
                {data.topProperties.length > 0 && (
                  <GlassCard className="p-4 print:border print:border-gray-300">
                    <h3 className="text-base font-semibold text-white print:text-black mb-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-cyan-400" /> Propiedades Más Vistas <InfoTooltip text="Propiedades con más interacciones (clicks en galería, contacto, etc)" />
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-white/10 print:border-gray-300"><th className="text-left py-2 px-2 text-white/60 print:text-gray-500 font-medium">Propiedad</th><th className="text-left py-2 px-2 text-white/60 print:text-gray-500 font-medium hidden sm:table-cell">Agente</th><th className="text-center py-2 px-2 text-white/60 print:text-gray-500 font-medium">Vistas</th><th className="text-center py-2 px-2 text-white/60 print:text-gray-500 font-medium">Interacciones</th></tr></thead>
                        <tbody>
                          {data.topProperties.map((prop) => (
                            <tr key={prop.id} className="border-b border-white/5 print:border-gray-200">
                              <td className="py-2 px-2 text-white print:text-black truncate max-w-[150px]">{prop.title}</td>
                              <td className="py-2 px-2 text-white/60 print:text-gray-500 hidden sm:table-cell">{prop.agentName}</td>
                              <td className="py-2 px-2 text-center text-cyan-400 print:text-cyan-600">{prop.uniqueViews}</td>
                              <td className="py-2 px-2 text-center text-green-400 print:text-green-600">{prop.interactions}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </GlassCard>
                )}

                {/* Tabla Detalle */}
                <GlassCard className="p-4 print:border print:border-gray-300">
                  <h3 className="text-base font-semibold text-white print:text-black mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-blue-400" /> Detalle por Agente</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-white/10 print:border-gray-300"><th className="text-left py-2 px-2 text-white/60 print:text-gray-500 font-medium">Agente</th><th className="text-center py-2 px-2 text-white/60 print:text-gray-500 font-medium">Sesiones</th><th className="text-center py-2 px-2 text-white/60 print:text-gray-500 font-medium">Leads</th><th className="text-center py-2 px-2 text-white/60 print:text-gray-500 font-medium hidden sm:table-cell">Props</th><th className="text-center py-2 px-2 text-white/60 print:text-gray-500 font-medium hidden sm:table-cell">Score</th></tr></thead>
                      <tbody>
                        {sellerRanking.map(seller => (
                          <tr key={seller.id} onClick={() => setSelectedSeller(seller.id)} className="border-b border-white/5 print:border-gray-200 hover:bg-white/5 cursor-pointer print:cursor-default">
                            <td className="py-2 px-2"><div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">{seller.name.charAt(0)}</div><span className="text-white print:text-black truncate max-w-[100px]">{seller.name}</span></div></td>
                            <td className="text-center py-2 px-2 text-white print:text-black">{seller.sessionsNum}</td>
                            <td className="text-center py-2 px-2"><span className={seller.leadsNum > 0 ? 'text-green-400 print:text-green-600 font-bold' : 'text-white/40 print:text-gray-400'}>{seller.leadsNum}</span></td>
                            <td className="text-center py-2 px-2 text-white print:text-black hidden sm:table-cell">{seller.properties}</td>
                            <td className="text-center py-2 px-2 hidden sm:table-cell"><span className={seller.scoreNum >= 80 ? 'text-green-400 print:text-green-600' : seller.scoreNum >= 50 ? 'text-yellow-400 print:text-yellow-600' : 'text-white/40 print:text-gray-400'}>{seller.scoreNum.toFixed(0)}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </GlassCard>
              </>
            ) : (
              <>
                {/* Vista individual */}
                <GlassCard className="p-4 print:border print:border-gray-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-lg font-bold">{selectedSellerData?.name.charAt(0)}</div>
                    <div><h3 className="text-lg font-bold text-white print:text-black">{selectedSellerData?.name}</h3><p className="text-white/60 print:text-gray-500 text-sm">{selectedSellerData?.subdomain}.innovagent.tech</p></div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[{ label: 'Sesiones', value: selectedSellerData?.sessions || 0, color: 'text-blue-400' }, { label: 'Leads', value: selectedSellerData?.leads || 0, color: 'text-green-400' }, { label: 'Propiedades', value: selectedSellerData?.properties || 0, color: 'text-purple-400' }, { label: 'Score', value: parseFloat(selectedSellerData?.avg_score || '0').toFixed(0), color: 'text-yellow-400' }].map((item, i) => (
                      <div key={i} className="bg-white/5 print:bg-gray-50 rounded-lg p-3 text-center"><p className={`text-2xl font-bold ${item.color} print:text-gray-800`}>{item.value}</p><p className="text-white/60 print:text-gray-500 text-xs">{item.label}</p></div>
                    ))}
                  </div>
                </GlassCard>
                <button onClick={() => setSelectedSeller('all')} className="w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm print:hidden"><Users className="h-4 w-4" /> Ver todos los agentes</button>
              </>
            )}

            {/* Footer para impresión */}
            <div className="hidden print:block text-center text-gray-400 text-xs mt-8 pt-4 border-t border-gray-300">
              Reporte generado por InnovAgent • {new Date().toLocaleDateString('es-MX')} • Período: {timeRange} días
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  )
}

export default AgencyAnalytics
